import { Image } from '@chakra-ui/react';
import {
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  Tag,
  Text,
  Container,
  useMediaQuery,
} from '@everlywell/ui-kit';
import SectionContainer from 'components/PageSections/components/SectionContainer';
import SectionHeader from 'components/PageSections/components/SectionHeader';
import { GatsbyImage } from 'gatsby-plugin-image';
import React from 'react';
import { sanitize } from 'utils/domHelpers';
import { ImageType } from 'utils/types';

export type BiomarkersCardsSectionProps = {
  backgroundColor?: string;
  title?: string;
  eyebrow?: string;
  cards: {
    title: string;
    description?: string;
    markers: string[];
    iconImage?: ImageType | null;
  }[];
  disclaimer?: string;
};

const BiomarkersCardsSection = ({
  backgroundColor = 'tints.lightCream',
  title,
  eyebrow,
  cards,
  disclaimer,
}: BiomarkersCardsSectionProps) => {
  const [isScreenNarrowForTags] = useMediaQuery(
    `(max-width: 680px) and (min-width: 600px)`,
  );
  return (
    <SectionContainer
      size={['sm', null, null, 'fluid']}
      flexDirection="column"
      gap={['10', '20', '20']}
      paddingY={['10', '20', '20']}
      backgroundColor={backgroundColor}
    >
      <Box>
        <SectionHeader title={title} eyebrow={eyebrow}></SectionHeader>
      </Box>

      <Container size={['fluid', null, null, 'xl']} margin="0 auto">
        <Grid
          columns={[1, 2, 2, 2]}
          spacing={['2']}
          justifyContent="flex-start"
        >
          {cards.map((card) => (
            <GridItem
              key={card.title}
              maxWidth="max-content"
              minWidth={['100%', '50%', '50%', '50%']}
            >
              <Flex
                maxWidth="100%"
                minHeight={['auto', null, null, '360px']}
                height="100%"
                backgroundColor="tints.white"
                padding="6"
                borderRadius="xl"
                flexDirection="column"
                gap={['8', null, null, 'unset']}
              >
                {card?.iconImage && (
                  <Image
                    as={GatsbyImage}
                    image={card?.iconImage?.gatsbyImageData}
                    aria-label={`${card.title} Biomarker icon`}
                    alt={`${card.title} Biomarker icon`}
                    width="56px"
                    height="56px"
                    marginBottom="auto"
                  />
                )}
                <Flex flexDirection="column" gap="6">
                  <Heading size="lg" fontWeight="normal">
                    {card.title}
                  </Heading>

                  {card?.description && (
                    <Text size="md" color="tints.darkGrey">
                      {card?.description}
                    </Text>
                  )}

                  <Flex flexDirection="row" gap="2" flexWrap="wrap">
                    {card.markers.map((marker) => (
                      <Tag
                        key={marker}
                        color="tints.darkGrey"
                        backgroundColor="tints.darkCream"
                        // Workaround to fix the tag's width
                        letterSpacing="-0.25px"
                        maxWidth={
                          isScreenNarrowForTags ? '239px' : 'max-content'
                        }
                      >
                        {marker}
                      </Tag>
                    ))}
                  </Flex>
                </Flex>
              </Flex>
            </GridItem>
          ))}
        </Grid>
      </Container>

      {disclaimer && (
        <Text
          size="md"
          color="tints.darkGrey"
          textAlign="center"
          marginTop={[10, 16, null, 20]}
          dangerouslySetInnerHTML={{ __html: sanitize(disclaimer) }}
        />
      )}
    </SectionContainer>
  );
};

export default BiomarkersCardsSection;
